/**
 * Helpers para obter URLs e configurações do Supabase
 * 
 * Centraliza a lógica de construção de URLs para webhooks e endpoints,
 * eliminando hardcodes de project_ref espalhados pelo código.
 * 
 * @release v1.0 - 2026-01-17
 */

/**
 * Obtém a URL base do Supabase a partir das variáveis de ambiente
 * Prioriza import.meta.env.VITE_SUPABASE_URL (aplicação principal)
 * 
 * @returns URL do Supabase (ex: https://xxxxx.supabase.co)
 */
export function getSupabaseUrl(): string {
  // Variável de ambiente do Vite (aplicação principal)
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL) {
    return import.meta.env.VITE_SUPABASE_URL;
  }
  
  // Fallback vazio (não deve ocorrer em produção)
  console.warn('[supabaseHelpers] URL do Supabase não configurada');
  return '';
}

/**
 * Constrói a URL de uma Edge Function do Supabase
 * 
 * @param functionName Nome da edge function (ex: 'instagram-webhook')
 * @returns URL completa da function (ex: https://xxxxx.supabase.co/functions/v1/instagram-webhook)
 */
export function getEdgeFunctionUrl(functionName: string): string {
  const baseUrl = getSupabaseUrl();
  
  if (!baseUrl) {
    console.warn('[supabaseHelpers] Não foi possível construir URL da edge function');
    return '';
  }
  
  // Remove barra final se existir para evitar duplicação
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  
  return `${cleanBaseUrl}/functions/v1/${functionName}`;
}

/**
 * Obtém a URL do webhook do Instagram
 * Conveniente wrapper para getEdgeFunctionUrl('instagram-webhook')
 * 
 * @returns URL do webhook do Instagram
 */
export function getInstagramWebhookUrl(): string {
  return getEdgeFunctionUrl('instagram-webhook');
}

/**
 * Obtém a URL do webhook do WhatsApp (Meta WABA)
 * Conveniente wrapper para getEdgeFunctionUrl('meta-waba-webhook')
 * 
 * @returns URL do webhook do WhatsApp Meta
 */
export function getMetaWabaWebhookUrl(): string {
  return getEdgeFunctionUrl('meta-waba-webhook');
}

/**
 * Obtém a URL do webhook de roteamento
 * Conveniente wrapper para getEdgeFunctionUrl('routing-webhook')
 * 
 * @returns URL do webhook de roteamento
 */
export function getRoutingWebhookUrl(): string {
  return getEdgeFunctionUrl('routing-webhook');
}

/**
 * Gera um token seguro para verificação de webhook
 * Usa crypto.getRandomValues() para gerar string aleatória segura
 * 
 * @param length Tamanho do token (padrão: 32 caracteres)
 * @returns Token aleatório seguro
 */
export function generateWebhookVerifyToken(length: number = 32): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  const cryptoArray = new Uint8Array(length);
  crypto.getRandomValues(cryptoArray);
  
  for (let i = 0; i < length; i++) {
    token += chars[cryptoArray[i] % chars.length];
  }
  
  return token;
}
